import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DoorCta from './DoorCta';
import useDoorPage, { studioHrefFor } from '../hooks/useDoorPage';
import { DOOR_LRTIMELAPSE_ALTERNATIVE } from '../doorAttribution';
import { RETENTION_DAYS } from '../photoUpload/caps';
import { PRICING_TIERS, FREE_SIGNUP_CREDITS } from '../pricing/plans';
import { CAPABILITY } from '../lib/operationCopy';
import { displayNameOf } from '../lib/operations';
import './DoorPage.css';

// LRTimelapse-alternative door (#387). Owns "LRTimelapse alternative",
// "LRTimelapse free alternative", "edit timelapse without Lightroom".
//
// COMPETITOR-PAGE RULES, and they are not optional:
//
//  1. NO PRICES, NO VERSION-SPECIFIC LIMITS for the other tool. They rot, and a
//     stale number on a page that names a competitor is the one factual error
//     that carries legal risk. "Paid desktop license" and "its free version has
//     image-count limits" are the strongest claims allowed here.
//  2. FRAME IT RESPECTFULLY AND ACCURATELY. LRTimelapse is a desktop
//     application with a keyframe workflow built around Adobe Lightroom, and it
//     is renowned for holy-grail day-to-night transitions. Saying so plainly is
//     both true and better positioning than a strawman.
//  3. THE "BETTER FIT" SECTION STAYS. A comparison that concedes nothing reads
//     as marketing and converts worse than one that tells a RAW-workflow
//     shooter to go use the other tool.
//  4. EVERY TWEEDIA CLAIM IS TRUE OF THE SHIPPED PRODUCT. Operation names come
//     from the registry (#379/#400), numbers from caps.js and plans.js.
//
// Absence claims about the other tool are deliberately rendered as an em dash
// with a legend ("difference in scope, not a defect") rather than as "No", so
// the table never asserts more than it can stand behind.

const FREE_TIER = PRICING_TIERS.find((tier) => tier.id === 'free');
const SOLO_TIER = PRICING_TIERS.find((tier) => tier.id === 'solo_founding');
const PRO_TIER = PRICING_TIERS.find((tier) => tier.id === 'pro_founding');

const FREE_POOL_LABEL = `${FREE_TIER.storageGb} GB`;

const FIX_FLICKER = displayNameOf('deflicker');
const TRIM_STILLNESS = displayNameOf('trim_stillness');

const OTHER = 'LRTimelapse';
// The em dash used for "this tool does not set out to do that". Rendered
// through a constant so the legend below and every cell that uses it agree.
const NOT_IN_SCOPE = '—';

const SECTION_IDS = [
  'door_hero',
  'door_what',
  'door_compare',
  'door_better_fit',
  'door_how',
  'door_free',
  'door_faq',
  'door_final_cta',
];

const TOC = [
  { id: 'door_what', label: 'What Tweedia Studio is' },
  { id: 'door_compare', label: `Side by side with ${OTHER}` },
  { id: 'door_better_fit', label: `When ${OTHER} is the better fit` },
  { id: 'door_how', label: 'How to switch, in four steps' },
  { id: 'door_free', label: 'What the free account includes' },
  { id: 'door_faq', label: 'FAQ' },
];

// [row label, Tweedia, LRTimelapse]
const COMPARISON = [
  {
    label: 'Where it runs',
    ours: 'In your browser, on any operating system',
    theirs: 'A desktop application you install and run locally',
  },
  {
    label: 'Install required',
    ours: 'None — sign in and start',
    theirs: 'Yes — a desktop installer',
  },
  {
    label: 'Adobe Lightroom required',
    ours: 'No. Nothing from Adobe is involved',
    theirs:
      'Its signature keyframe workflow round-trips through Lightroom or Camera Raw',
  },
  {
    label: 'What you feed it',
    ours:
      '.mp4 video files, and folders of stills (JPEG, PNG, HEIC/HEIF, TIFF, WebP, AVIF)',
    theirs: 'Image sequences, RAW first',
  },
  {
    label: 'Flicker',
    ours: `${FIX_FLICKER} — flicker is measured automatically and flagged on the card; one switch applies it`,
    theirs: 'Yes. Its visual deflicker is one of the things it is known for',
  },
  {
    label: 'Trimming the stretches where nothing happens',
    ours: `${TRIM_STILLNESS}, driven by ${CAPABILITY.feature}`,
    theirs: NOT_IN_SCOPE,
  },
  {
    label: 'Holy-grail day-to-night keyframing',
    ours: NOT_IN_SCOPE,
    theirs: 'Yes — this is what it is renowned for',
  },
  {
    label: 'RAW and XMP round trip',
    ours: NOT_IN_SCOPE,
    theirs: 'Yes, the core of the workflow',
  },
  {
    label: 'Works offline',
    ours: NOT_IN_SCOPE,
    theirs: 'Yes — everything happens on your own machine',
  },
  {
    label: 'Price to start',
    ours: 'Free account. Renders carry a Tweedia watermark until you subscribe',
    theirs: 'Paid desktop license; its free version has image-count limits',
  },
];

const BETTER_FIT = [
  {
    term: 'Holy-grail day-to-night transitions',
    text: `Ramping exposure across a sunset with keyframes, so a shoot that spans four stops of light lands as one continuous clip, is ${OTHER}'s home ground. Tweedia has no keyframe grading and does not pretend to.`,
  },
  {
    term: 'RAW workflows',
    text: 'If your sequence is RAW files you intend to grade in Lightroom or Camera Raw and round-trip as XMP metadata, that pipeline exists there and does not exist here. Tweedia works on rendered images and video.',
  },
  {
    term: 'Working offline, or on footage you cannot upload',
    text: 'Tweedia is a hosted service: the frames go to our servers. A shoot under an NDA, or an edit on a boat with no bandwidth, wants a local application.',
  },
  {
    term: 'Hands on the deflicker curve',
    text: `${OTHER} gives you manual control over how its deflicker behaves. ${FIX_FLICKER} is deliberately one switch — the trade is control for not having to think about it.`,
  },
];

const STEPS = [
  {
    name: 'Create a free account',
    text: 'No credit card, no installer, no license key. An account is required before anything is uploaded.',
  },
  {
    name: 'Start a project and upload what you already have',
    text: 'A rendered .mp4 goes straight in. A folder of stills goes in as a folder — the frames are ordered by the time each one was captured and compiled into a video at the frame rate you pick.',
  },
  {
    name: 'Edit on the Edits tab',
    text: `One card per operation, in the order they are applied: ${TRIM_STILLNESS}, ${FIX_FLICKER}, and transitions between clips. Each card shows what it will do before you render.`,
  },
  {
    name: 'Render and download',
    text: 'Rendering runs on our workers, so a long edit does not tie up your machine. Free-plan renders carry a Tweedia watermark; a paid plan removes it.',
  },
];

const FAQ = [
  {
    q: `Is Tweedia Studio a drop-in replacement for ${OTHER}?`,
    a: `No, and it is worth being straight about that. ${OTHER} is a keyframe grading tool for RAW sequences with a Lightroom round trip at its centre. Tweedia is a browser workspace that takes footage you have already shot — video or stills — and does the mechanical edit: trim the stillness, fix the flicker, join the clips, render. If keyframed exposure ramping is the reason you use ${OTHER}, keep using it.`,
  },
  {
    q: 'Can I edit a timelapse without Lightroom?',
    a: 'Yes. Nothing in Tweedia touches Adobe software. You upload an .mp4 or a folder of images, choose your operations, and download the render.',
  },
  {
    q: 'Do I need to install anything?',
    a: 'No — Tweedia runs in the browser and renders on our servers, so a laptop that would struggle with a desktop timelapse application is not a constraint.',
  },
  {
    q: 'What does the free tier actually give me?',
    a: `A working account: ${FREE_TIER.storageGb} GB of pooled storage across unlimited projects, ${FREE_SIGNUP_CREDITS} starter credits toward rendering, and every operation unlocked. The catch is honest and singular — free renders carry a Tweedia watermark, and raw photos are cleared ${RETENTION_DAYS} days after a project's last activity.`,
  },
  {
    q: 'Can it handle a long shoot?',
    a: `There is no per-project frame limit. The one ceiling is your account's pooled storage — ${FREE_POOL_LABEL} free, ${SOLO_TIER.storageGb} GB on ${SOLO_TIER.name}, ${PRO_TIER.storageGb} GB on ${PRO_TIER.name} — shared across every project, and the uploader tells you before a single file moves if a batch will not fit.`,
  },
  {
    q: 'Are you affiliated with LRTimelapse?',
    a: 'No. This is a comparison page written by Tweedia. We have described the other tool as accurately as we can and have deliberately not quoted its prices or version-specific limits, because those change.',
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/lrtimelapse-alternative';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: `${OTHER} Alternative`,
        description:
          'A browser-based timelapse editor that needs no install and no Lightroom: upload video or stills, fix flicker, trim stillness, and render.',
        url,
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

const STUDIO_HREF = studioHrefFor(DOOR_LRTIMELAPSE_ALTERNATIVE);

function LrtAlternativePage({ user, signIn }) {
  const signedIn = !!user;

  const { studioHref, setSectionRef, onCtaActivate, onNavClick } = useDoorPage({
    door: DOOR_LRTIMELAPSE_ALTERNATIVE,
    signedIn,
    signIn,
    sectionIds: SECTION_IDS,
    meta: {
      title: 'Free LRTimelapse Alternative (No Lightroom) | Tweedia Studio',
      description: `A browser-based ${OTHER} alternative: no install, no Lightroom. Work straight from an .mp4 or a folder of stills, fix flicker, trim stillness, render free.`,
      canonicalPath: '/lrtimelapse-alternative',
      jsonLd: buildJsonLd(),
    },
  });

  const renderCta = (ctaId, label, testId) => (
    <DoorCta
      ctaId={ctaId}
      testId={testId}
      signedIn={signedIn}
      studioHref={studioHref}
      onActivate={onCtaActivate}
    >
      {label}
    </DoorCta>
  );

  return (
    <div className="door-page">
      <Header user={user} signIn={signIn} />
      <main className="door-content" data-testid="door-content">
        <section
          className="door-hero"
          id="door_hero"
          data-section-id="door_hero"
          ref={setSectionRef('door_hero')}
        >
          <p className="door-eyebrow">{OTHER} alternative</p>
          <h1>A browser-based {OTHER} alternative</h1>
          <p className="door-lede">
            Tweedia Studio edits timelapses in a browser tab. Nothing to install, no Lightroom,
            no license key — upload an .mp4 or a folder of stills, switch on {FIX_FLICKER},{' '}
            {CAPABILITY.verb}, and render. The account is free; free renders carry a watermark.
          </p>
          <div className="door-cta-row">
            {renderCta('door_lrt_hero', 'Start free in your browser', 'door-cta-hero')}
          </div>
          <p className="door-note">
            Free account, no credit card. This page is a comparison, not a claim that the two
            tools do the same job — the differences are below, including where {OTHER} wins.
          </p>
        </section>

        <nav className="door-toc" aria-label="On this page">
          <p className="door-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="door_what" data-section-id="door_what" ref={setSectionRef('door_what')}>
          <h2>What Tweedia Studio is</h2>
          <p>
            A hosted workspace for the part of timelapse that comes after the shoot. You bring
            footage you already have — a rendered clip, or the folder of frames your camera
            left on the card — and Tweedia does the mechanical edit: {CAPABILITY.verb} so the
            hours where nothing moved do not eat the runtime, run {FIX_FLICKER} so the
            brightness stops jittering, crossfade the joins, and render.
          </p>
          <p>
            {OTHER} solves a different problem, and solves it well: it is a desktop application
            whose keyframe workflow, built around Adobe Lightroom, grades a RAW sequence frame
            by frame. If that is the problem you have, it is the tool. If your problem is that
            you have footage and want a finished video without installing anything, keep
            reading.
          </p>
        </section>

        <section
          id="door_compare"
          data-section-id="door_compare"
          ref={setSectionRef('door_compare')}
        >
          <h2>Side by side with {OTHER}</h2>
          <div className="door-table-scroll">
            <table className="door-table" data-testid="door-compare-table">
              <caption className="door-note">
                {NOT_IN_SCOPE} means the tool does not set out to do that — a difference in
                scope, not a defect.
              </caption>
              <thead>
                <tr>
                  <th scope="col">&nbsp;</th>
                  <th scope="col" className="door-table-ours">
                    Tweedia Studio
                  </th>
                  <th scope="col">{OTHER}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td className="door-table-ours">{row.ours}</td>
                    <td>{row.theirs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Deliberately absent from that table: {OTHER}'s prices and the exact limits of its
            free version. Both change, and a comparison page that quotes a stale number is
            worse than one that does not quote it at all. Check its own site for current terms.
          </p>
        </section>

        <section
          id="door_better_fit"
          data-section-id="door_better_fit"
          ref={setSectionRef('door_better_fit')}
        >
          <h2>When {OTHER} is the better fit</h2>
          <p>
            Four cases where we would tell you to use it instead. None of them are on our
            roadmap as a way to catch up.
          </p>
          <dl className="door-causes" data-testid="door-better-fit">
            {BETTER_FIT.map((item) => (
              <React.Fragment key={item.term}>
                <dt>{item.term}</dt>
                <dd>{item.text}</dd>
              </React.Fragment>
            ))}
          </dl>
        </section>

        <section id="door_how" data-section-id="door_how" ref={setSectionRef('door_how')}>
          <h2>How to switch, in four steps</h2>
          <ol className="door-steps" data-testid="door-steps">
            {STEPS.map((step) => (
              <li key={step.name}>
                <strong>{step.name}.</strong> {step.text}
              </li>
            ))}
          </ol>
          <div className="door-crosslinks" data-testid="door-crosslinks">
            <Link
              to="/deflicker-timelapse"
              className="door-crosslink"
              data-testid="door-link-deflicker"
              onClick={onNavClick('deflicker_door_from_lrt')}
            >
              <span className="door-crosslink-title">Deflicker a timelapse →</span>
              <span className="door-crosslink-text">
                What causes flicker, and what {FIX_FLICKER} does about it.
              </span>
            </Link>
            <Link
              to="/photo-sequence-to-video"
              className="door-crosslink"
              data-testid="door-link-photo-sequence"
              onClick={onNavClick('photo_sequence_door_from_lrt')}
            >
              <span className="door-crosslink-title">Photo sequence to video →</span>
              <span className="door-crosslink-text">
                Bringing a folder of stills in, ordered by capture time.
              </span>
            </Link>
          </div>
        </section>

        <section id="door_free" data-section-id="door_free" ref={setSectionRef('door_free')}>
          <h2>What the free account includes</h2>
          <ul className="door-free-list" data-testid="door-free-list">
            <li>
              <strong>Every operation unlocked</strong> — {TRIM_STILLNESS}, {FIX_FLICKER} and
              transitions are not held back for paid accounts.
            </li>
            <li>
              <strong>{FREE_POOL_LABEL} of pooled storage</strong> across unlimited projects,
              with no per-project frame limit.
            </li>
            <li>
              <strong>{FREE_SIGNUP_CREDITS} starter credits</strong> toward rendering. A longer
              or higher-resolution render uses more of them.
            </li>
            <li>
              <strong>Renders carry a Tweedia watermark</strong> on the free plan. Resolution is
              not the limit — every plan exports up to 4K.
            </li>
            <li>
              <strong>Raw photos are kept {RETENTION_DAYS} days</strong> after a project's last
              activity on the free plan, then cleared.
            </li>
          </ul>
          <p>
            {SOLO_TIER.name} at {SOLO_TIER.priceLabel}
            {SOLO_TIER.priceSuffix} removes the watermark and raises the pool to{' '}
            {SOLO_TIER.storageGb} GB; {PRO_TIER.name} at {PRO_TIER.priceLabel}
            {PRO_TIER.priceSuffix} raises it to {PRO_TIER.storageGb} GB.{' '}
            <Link to="/pricing" onClick={onNavClick('pricing_from_lrt_door')}>
              See the plans
            </Link>
            .
          </p>
        </section>

        <section id="door_faq" data-section-id="door_faq" ref={setSectionRef('door_faq')}>
          <h2>FAQ</h2>
          <div className="door-faq" data-testid="door-faq">
            {FAQ.map((item) => (
              <div className="door-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="door-final-cta"
          id="door_final_cta"
          data-section-id="door_final_cta"
          ref={setSectionRef('door_final_cta')}
        >
          <h2>Try it on a clip you already have</h2>
          <p>
            Upload one shoot, run it through, and see whether the browser workflow covers what
            you need. Nothing to uninstall if it does not.
          </p>
          <div className="door-cta-row">
            {renderCta('door_lrt_final', 'Create your free account', 'door-cta-final')}
            <Link
              to="/pricing"
              className="door-secondary-cta"
              data-testid="door-pricing-link"
              onClick={onNavClick('pricing_from_lrt_door_final')}
            >
              See pricing
            </Link>
          </div>
          <p className="door-note">
            Free renders carry a Tweedia watermark. No credit card required to start.
          </p>
        </section>

        <p className="door-footnote" data-testid="door-footnote">
          {OTHER} is a trademark of its respective owner, and Adobe, Lightroom and Camera Raw
          are trademarks of Adobe Inc. Tweedia Studio is not affiliated with, endorsed by or
          sponsored by {OTHER} or Adobe. Product descriptions here are our own summary, written
          in {new Date().getFullYear()}; check each product's own site for current features and
          terms.
        </p>
      </main>
      <Footer />
    </div>
  );
}

export default LrtAlternativePage;
export { COMPARISON, BETTER_FIT, FAQ, STEPS, SECTION_IDS, STUDIO_HREF, OTHER, NOT_IN_SCOPE };
